<template>
  <v-text-field
    :model-value="modelValue"
    :label="label"
    :placeholder="placeholder"
    :disabled="disabled"
    :hide-details="hideDetails"
    variant="outlined"
    density="comfortable"
    maxlength="7"
    @update:model-value="aoDigitar"
    @blur="normalizar"
  >
    <!-- Amostra clicável: abre a paleta. Serve também de pré-visualização. -->
    <template #prepend-inner>
      <v-menu v-model="aberto" :close-on-content-click="false" location="bottom start" offset="8">
        <template #activator="{ props }">
          <button
            v-bind="props"
            type="button"
            class="amostra-cor"
            :class="{ 'amostra-cor--vazia': !corValida }"
            :style="corValida ? { backgroundColor: modelValue } : null"
            :disabled="disabled"
            :aria-label="`Escolher ${label.toLowerCase()}`"
          />
        </template>

        <v-card rounded="lg" elevation="8" class="pa-3">
          <div class="text-caption font-weight-bold text-uppercase text-grey-darken-2 mb-2">
            Cores da instituição
          </div>
          <div class="paleta mb-3">
            <button
              v-for="cor in paletaInstitucional"
              :key="cor"
              type="button"
              class="paleta-cor"
              :class="{ 'paleta-cor--ativa': ehAtual(cor) }"
              :style="{ backgroundColor: cor }"
              :title="cor"
              @click="escolher(cor)"
            />
          </div>

          <v-divider class="mb-3" />

          <v-color-picker
            :model-value="corValida ? modelValue : placeholder"
            mode="hex"
            :modes="['hex']"
            :swatches="paletaAmpla"
            show-swatches
            swatches-max-height="140"
            width="290"
            @update:model-value="escolher"
          />

          <div class="d-flex justify-end mt-2">
            <v-btn variant="text" size="small" @click="limpar">Limpar</v-btn>
            <v-btn variant="text" size="small" color="primary" @click="aberto = false">Pronto</v-btn>
          </div>
        </v-card>
      </v-menu>
    </template>
  </v-text-field>
</template>

<script>
export default {
  name: "SeletorCor",

  props: {
    modelValue: { type: String, default: "" },
    label: { type: String, default: "Cor" },
    // Também é a cor que o seletor abre quando o campo está vazio.
    placeholder: { type: String, default: "#0D47A1" },
    disabled: { type: Boolean, default: false },
    hideDetails: { type: [Boolean, String], default: false },
  },

  emits: ["update:modelValue"],

  data() {
    return {
      aberto: false,
      // Cores da identidade da AOM e tons de apoio, para o caso comum.
      paletaInstitucional: [
        "#0D47A1", "#1565C0", "#1E7C73", "#2E7D32",
        "#FFB300", "#EF6C00", "#C62828", "#6A1B9A",
        "#37474F", "#000000",
      ],
      // Paleta ampla do próprio seletor, em linhas.
      paletaAmpla: [
        ["#0D47A1", "#1565C0", "#1976D2", "#42A5F5"],
        ["#1E7C73", "#00897B", "#2E7D32", "#66BB6A"],
        ["#FFB300", "#FB8C00", "#EF6C00", "#F4511E"],
        ["#C62828", "#AD1457", "#6A1B9A", "#4527A0"],
        ["#212121", "#37474F", "#757575", "#BDBDBD"],
      ],
    };
  },

  computed: {
    corValida() {
      return /^#[0-9a-fA-F]{6}$/.test(this.modelValue || "");
    },
  },

  methods: {
    ehAtual(cor) {
      return (this.modelValue || "").toUpperCase() === cor.toUpperCase();
    },

    escolher(cor) {
      if (!cor) return;
      // O seletor pode devolver 8 dígitos (com alfa); guardamos só o RGB.
      this.$emit("update:modelValue", String(cor).slice(0, 7).toUpperCase());
    },

    // Aceita digitação sem "#" e ignora caracteres que não são hexadecimais,
    // para que colar "0D47A1" ou "#0d47a1 " funcione do mesmo jeito.
    aoDigitar(valor) {
      const limpo = String(valor || "").replace(/[^0-9a-fA-F]/g, "").slice(0, 6);
      this.$emit("update:modelValue", limpo ? `#${limpo.toUpperCase()}` : "");
    },

    normalizar() {
      if (this.modelValue && !this.corValida) {
        this.$emit("update:modelValue", "");
      }
    },

    limpar() {
      this.$emit("update:modelValue", "");
      this.aberto = false;
    },
  },
};
</script>

<style scoped>
.amostra-cor {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.22);
  cursor: pointer;
  flex: 0 0 auto;
}

.amostra-cor:disabled {
  cursor: default;
  opacity: 0.5;
}

/* Campo vazio: xadrez discreto, para não parecer uma cor escolhida. */
.amostra-cor--vazia {
  background-image: linear-gradient(45deg, #ccc 25%, transparent 25%),
    linear-gradient(-45deg, #ccc 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #ccc 75%),
    linear-gradient(-45deg, transparent 75%, #ccc 75%);
  background-size: 10px 10px;
  background-position: 0 0, 0 5px, 5px -5px, -5px 0;
  background-color: #fff;
}

.paleta {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
}

.paleta-cor {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.18);
  cursor: pointer;
}

.paleta-cor--ativa {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}
</style>
